import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;
const EMOJIS = { Bags:'🎒',Drinkware:'🥤',Apparel:'👕',Tech:'⚡',Office:'📓' };
const STORAGE_KEY_ITEMS = 'ps_request_items';
const STORAGE_KEY_LOGO  = 'ps_request_logo';
const STORAGE_KEY_NOTES = 'ps_request_notes';
const DESKTOP_BREAKPOINT = 768;
const LOGO_MAX_BYTES = 2 * 1024 * 1024; // 2MB

// ─── Helpers ──────────────────────────────────────────────────────────────────
const hexBg = (hex, alpha = '', fallback = '#F8FAFC') => {
  if (!hex) return fallback;
  const clean = hex.startsWith('#') ? hex : `#${hex}`;
  return clean + alpha;
};

const cardImageUrl = (p) => {
  const imgs = p.images || [];
  return imgs.find(i => i.type === 'primary')?.url || imgs[0]?.url || null;
};

const buildGallery = (p) => {
  const imgs = p.images || [];
  const out = [];
  const seen = new Set();
  const push = (img) => { if (img?.url && !seen.has(img.url)) { seen.add(img.url); out.push(img); } };
  imgs.filter(i => i.type === 'primary').forEach(push);
  const seenParts = new Set();
  imgs.filter(i => i.type === 'blank').forEach(i => {
    if (!seenParts.has(i.partId)) { seenParts.add(i.partId); push(i); }
  });
  imgs.filter(i => i.type === 'decorated').slice(0, 4).forEach(push);
  for (const i of imgs) { if (out.length >= 12) break; push(i); }
  return out;
};

// Also strips HTML some suppliers send in descriptions (<ul><li>…). The sync
// now stores plain text, but rows synced before that fix still carry tags.
const cleanDescription = (d) =>
  (d || '')
    .replace(/<\s*li[^>]*>/gi, ' • ').replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/&sup2;/gi, '\u00b2')
    .replace(/\s+\./g, '.').replace(/\s+,/g, ',').replace(/\s{2,}/g, ' ').trim();

// Read a file as base64 data URL with size check
const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
  if (!file) return reject(new Error('No file'));
  if (!file.type.startsWith('image/')) return reject(new Error('Must be an image file'));
  if (file.size > LOGO_MAX_BYTES) return reject(new Error('Image too large (max 2MB)'));
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = () => reject(new Error('Failed to read file'));
  r.readAsDataURL(file);
});

// ─── Color picker (custom dropdown so swatches can render inline) ────────────
// Dropdown is rendered with position:fixed so it floats above the scroll
// container's overflow boundary (otherwise the parent rail tries to scroll
// the whole row when the dropdown opens).
function ColorPicker({ colors, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, openUp: false });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Compute where the floating dropdown should render based on the trigger's
  // position. Re-runs on open, scroll, and resize so it stays anchored.
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = 240; // matches maxHeight below
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < dropdownHeight + 20 && r.top > dropdownHeight;
    setPos({
      top: openUp ? r.top - 4 : r.bottom + 4,
      left: r.left,
      width: Math.max(r.width, 180),
      openUp,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const closeOnOutside = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    // Close on any scroll outside the dropdown itself, since fixed positioning
    // means the dropdown won't follow if the page scrolls.
    const closeOnScroll = (e) => {
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutside);
    window.addEventListener('scroll', closeOnScroll, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      window.removeEventListener('scroll', closeOnScroll, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  if (!colors || colors.length === 0) return null;

  const selected = value && colors.find(c => c.name === value.name);

  return (
    <>
      <button ref={triggerRef} onClick={()=>setOpen(o=>!o)}
        style={{ display:'flex',alignItems:'center',gap:5,height:24,
          border:'1px solid #E2E8F0',borderRadius:5,padding:'2px 8px',
          background:'#fff',cursor:'pointer',fontFamily:'inherit',fontSize:11,color:'#0F172A',
          minWidth:80,maxWidth:120,flexShrink:0 }}>
        {selected ? (
          <>
            <span style={{ width:10,height:10,borderRadius:'50%',background:hexBg(selected.hex,'','#ccc'),border:'1px solid #E2E8F0',flexShrink:0 }}/>
            <span style={{ overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,textAlign:'left' }}>{selected.name}</span>
          </>
        ) : (
          <span style={{ color:'#94A3B8',flex:1,textAlign:'left' }}>Any color</span>
        )}
        <span style={{ color:'#94A3B8',fontSize:9 }}>▾</span>
      </button>
      {open && (
        <div ref={dropdownRef}
          style={{ position:'fixed',
            top: pos.openUp ? 'auto' : pos.top,
            bottom: pos.openUp ? (window.innerHeight - pos.top) : 'auto',
            left: pos.left,
            minWidth: pos.width,
            zIndex: 1000,
            background:'#fff',border:'1px solid #E2E8F0',borderRadius:8,
            boxShadow:'0 8px 24px rgba(15,23,42,0.12)',
            maxHeight:240,overflowY:'auto',
            padding:'4px 0' }}>
          <div onClick={()=>{onChange(null);setOpen(false);}}
            style={{ padding:'7px 12px',fontSize:12,cursor:'pointer',color:'#64748B',
              display:'flex',alignItems:'center',gap:8 }}
            onMouseEnter={e=>e.currentTarget.style.background='#F8FAFC'}
            onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
            <span style={{ width:14,height:14,borderRadius:'50%',background:'#fff',border:'1px dashed #CBD5E1' }}/>
            Any color
          </div>
          {colors.map((c,i) => (
            <div key={i} onClick={()=>{onChange({name:c.name,hex:c.hex});setOpen(false);}}
              style={{ padding:'7px 12px',fontSize:12,cursor:'pointer',color:'#0F172A',
                display:'flex',alignItems:'center',gap:8,
                background: selected?.name===c.name ? `${G}10` : 'transparent' }}
              onMouseEnter={e=>{ if (selected?.name!==c.name) e.currentTarget.style.background='#F8FAFC'; }}
              onMouseLeave={e=>{ if (selected?.name!==c.name) e.currentTarget.style.background='transparent'; }}>
              <span style={{ width:14,height:14,borderRadius:'50%',background:hexBg(c.hex,'','#ccc'),border:'1px solid #E2E8F0',flexShrink:0 }}/>
              <span style={{ overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{c.name}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Loading / error states ───────────────────────────────────────────────────
function Loading() {
  return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#F8FAFC' }}>
      <div style={{ fontSize:13,color:'#94A3B8' }}>Loading storefront...</div>
    </div>
  );
}

function NotFound({ slug }) {
  return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#F8FAFC',padding:24 }}>
      <div style={{ background:'#fff',borderRadius:12,border:'1px solid #E2E8F0',padding:40,maxWidth:420,textAlign:'center' }}>
        <div style={{ fontSize:48,marginBottom:12 }}>🤷</div>
        <div style={{ fontSize:18,fontWeight:600,color:'#0F172A',marginBottom:8 }}>Storefront not found</div>
        <div style={{ fontSize:13,color:'#64748B',lineHeight:1.6 }}>
          We couldn't find a storefront at <strong style={{ color:'#0F172A' }}>/store/{slug}</strong>.
          Check the URL or contact the business directly.
        </div>
      </div>
    </div>
  );
}

// ─── Request builder content (shared between desktop rail and mobile sheet) ──
function RequestBuilder({ items, logo, notes, onRemove, onChangeQty, onChangeColor, onUploadLogo, onRemoveLogo, onChangeNotes, onSend, distName }) {
  const fileRef = useRef(null);
  const [logoError, setLogoError] = useState(null);

  const handleFile = async (e) => {
    setLogoError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await readFileAsDataURL(file);
      onUploadLogo(dataUrl);
    } catch (err) {
      setLogoError(err.message);
    }
    e.target.value = '';
  };

  return (
    <>
      {/* Logo upload */}
      <div style={{ padding:'18px 20px',borderBottom:'1px solid #F1F5F9' }}>
        <div style={{ fontSize:11,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:10 }}>Your logo (optional)</div>
        {logo ? (
          <div style={{ position:'relative',width:'fit-content' }}>
            <img src={logo} alt="Your logo"
              style={{ maxWidth:140,maxHeight:80,borderRadius:8,border:'1px solid #E2E8F0',background:'#fff',padding:6 }}/>
            <button onClick={onRemoveLogo} aria-label="Remove logo"
              style={{ position:'absolute',top:-8,right:-8,width:22,height:22,borderRadius:'50%',
                border:'1px solid #E2E8F0',background:'#fff',cursor:'pointer',fontSize:14,
                color:'#64748B',display:'flex',alignItems:'center',justifyContent:'center',padding:0,fontFamily:'inherit',
                boxShadow:'0 2px 6px rgba(0,0,0,0.08)' }}>×</button>
          </div>
        ) : (
          <>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display:'none' }}/>
            <button onClick={()=>fileRef.current?.click()}
              style={{ width:'100%',padding:'18px 12px',border:'1px dashed #CBD5E1',borderRadius:10,
                background:'#F8FAFC',cursor:'pointer',fontSize:12,color:'#64748B',fontFamily:'inherit',
                display:'flex',flexDirection:'column',alignItems:'center',gap:4 }}>
              <span style={{ fontSize:18 }}>📷</span>
              <span>+ Upload your logo</span>
              <span style={{ fontSize:10,color:'#94A3B8' }}>PNG/JPG, up to 2MB</span>
            </button>
          </>
        )}
        {logoError && <div style={{ fontSize:11,color:'#EF4444',marginTop:6 }}>{logoError}</div>}
      </div>

      {/* Items */}
      <div style={{ padding:'18px 20px',borderBottom:'1px solid #F1F5F9',flex:1,minHeight:0,overflowY:'auto' }}>
        <div style={{ fontSize:11,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:12 }}>
          Items ({items.length})
        </div>
        {items.length === 0 ? (
          <div style={{ textAlign:'center',padding:'24px 12px',color:'#94A3B8' }}>
            <div style={{ fontSize:36,marginBottom:8,opacity:0.5 }}>✨</div>
            <div style={{ fontSize:13,fontWeight:500,color:'#64748B',marginBottom:4 }}>Nothing added yet</div>
            <div style={{ fontSize:12,lineHeight:1.5 }}>Tap <strong>+ Add</strong> on any product to start building your quote request.</div>
          </div>
        ) : (
          <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
            {items.map(item => (
              <div key={item.id} style={{ display:'flex',gap:10,alignItems:'flex-start',padding:8,borderRadius:8,background:'#F8FAFC' }}>
                <div style={{ width:44,height:44,borderRadius:6,background:'#fff',border:'1px solid #E2E8F0',
                  display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,overflow:'hidden' }}>
                  {item.image ? (
                    <img src={item.image} alt={item.name}
                      style={{ maxWidth:'100%',maxHeight:'100%',objectFit:'contain' }}/>
                  ) : (
                    <span style={{ fontSize:20 }}>{EMOJIS[item.category]||'📦'}</span>
                  )}
                </div>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ fontSize:12,fontWeight:500,color:'#0F172A',lineHeight:1.3,
                    display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden' }}>
                    {item.name}
                  </div>
                  <div style={{ display:'flex',alignItems:'center',gap:8,marginTop:6,flexWrap:'wrap' }}>
                    <div style={{ display:'flex',alignItems:'center',gap:5,flexShrink:0 }}>
                      <span style={{ fontSize:10,color:'#94A3B8' }}>Qty:</span>
                      <input type="number" min="1" placeholder="—"
                        value={item.qty || ''}
                        onChange={e=>onChangeQty(item.id, e.target.value ? parseInt(e.target.value,10) : null)}
                        style={{ width:54,height:24,border:'1px solid #E2E8F0',borderRadius:5,
                          padding:'2px 6px',fontSize:11,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff' }}/>
                    </div>
                    {(item.colors?.length > 0) && (
                      <ColorPicker colors={item.colors} value={item.color}
                        onChange={(c) => onChangeColor(item.id, c)}/>
                    )}
                  </div>
                </div>
                <button onClick={()=>onRemove(item.id)} aria-label="Remove"
                  style={{ width:24,height:24,borderRadius:6,border:'none',background:'transparent',
                    cursor:'pointer',fontSize:16,color:'#94A3B8',padding:0,fontFamily:'inherit',flexShrink:0 }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notes */}
      <div style={{ padding:'18px 20px',borderBottom:'1px solid #F1F5F9' }}>
        <div style={{ fontSize:11,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:10 }}>Tell us about your project</div>
        <textarea value={notes} onChange={e=>onChangeNotes(e.target.value)}
          placeholder="Event date, target colors, budget, deadlines, anything else we should know..."
          rows={4}
          style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'9px 12px',fontSize:12,
            fontFamily:'inherit',outline:'none',color:'#0F172A',resize:'vertical',boxSizing:'border-box',background:'#fff' }}/>
      </div>

      {/* Send CTA */}
      <div style={{ padding:'16px 20px' }}>
        <button onClick={onSend} disabled={items.length === 0}
          style={{ width:'100%',background: items.length===0 ? '#CBD5E1' : G,
            border:'none',borderRadius:10,padding:'13px 20px',
            color:'#fff',fontSize:14,fontWeight:600,cursor: items.length===0 ? 'not-allowed' : 'pointer',
            fontFamily:'inherit',transition:'all 0.15s' }}>
          Send Request →
        </button>
        {distName && (
          <div style={{ fontSize:11,color:'#94A3B8',textAlign:'center',marginTop:8 }}>
            Goes to {distName} · they respond within 24 hours
          </div>
        )}
      </div>
    </>
  );
}

// ─── Right rail (desktop ≥768px) ─────────────────────────────────────────────
function RequestRail(props) {
  return (
    <aside style={{ position:'sticky',top:24,alignSelf:'flex-start',
      width:340,flexShrink:0,
      background:'#fff',borderRadius:14,border:'1px solid #E2E8F0',
      display:'flex',flexDirection:'column',
      maxHeight:'calc(100vh - 48px)',overflow:'hidden' }}>
      <div style={{ padding:'18px 20px',borderBottom:'1px solid #F1F5F9' }}>
        <div style={{ fontSize:13,fontWeight:600,color:'#0F172A',letterSpacing:'-0.2px' }}>Your Request</div>
        <div style={{ fontSize:11,color:'#94A3B8',marginTop:2 }}>
          {props.items.length === 0
            ? 'Build a quote — add products to get started'
            : `Building your quote — ${props.items.length} item${props.items.length===1?'':'s'}`}
        </div>
      </div>
      <RequestBuilder {...props} />
    </aside>
  );
}

// ─── Mobile bottom bar + sheet (<768px) ──────────────────────────────────────
function MobileRequestBar({ items, logo, notes, onRemove, onChangeQty, onChangeColor, onUploadLogo, onRemoveLogo, onChangeNotes, onSend, distName }) {
  const [open, setOpen] = useState(false);
  const empty = items.length === 0;

  return (
    <>
      {/* Compact bar — always visible. Empty state shows hint, populated shows thumbs. */}
      <div onClick={()=>!empty && setOpen(true)}
        style={{ position:'fixed',bottom:0,left:0,right:0,zIndex:60,
          background:'#fff',borderTop:'1px solid #E2E8F0',
          boxShadow:'0 -4px 16px rgba(15,23,42,0.06)',
          padding: empty ? '8px 16px' : '10px 16px',
          display:'flex',alignItems:'center',gap:10,
          cursor: empty ? 'default' : 'pointer',
          minHeight: empty ? 36 : 56 }}>
        {empty ? (
          <div style={{ fontSize:12,color:'#64748B',textAlign:'center',width:'100%' }}>
            Tap <strong style={{ color:'#0F172A' }}>+ Add</strong> on any product to build your request
          </div>
        ) : (
          <>
            {/* Thumbnail strip — up to 4 visible, then "+N" */}
            <div style={{ display:'flex',gap:4,flexShrink:0 }}>
              {items.slice(0,4).map(it => (
                <div key={it.id} style={{ width:36,height:36,borderRadius:6,background:'#F8FAFC',
                  border:'1px solid #E2E8F0',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden' }}>
                  {it.image ? (
                    <img src={it.image} alt="" style={{ maxWidth:'100%',maxHeight:'100%',objectFit:'contain' }}/>
                  ) : (
                    <span style={{ fontSize:18 }}>{EMOJIS[it.category]||'📦'}</span>
                  )}
                </div>
              ))}
              {items.length > 4 && (
                <div style={{ width:36,height:36,borderRadius:6,background:'#F1F5F9',border:'1px solid #E2E8F0',
                  display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:600,color:'#475569' }}>
                  +{items.length - 4}
                </div>
              )}
            </div>
            <div style={{ flex:1,minWidth:0 }}>
              <div style={{ fontSize:13,fontWeight:600,color:'#0F172A' }}>
                {items.length} item{items.length===1?'':'s'}
              </div>
              <div style={{ fontSize:11,color:'#94A3B8' }}>Tap to review →</div>
            </div>
            <button onClick={(e)=>{e.stopPropagation();onSend();}}
              style={{ background:G,border:'none',borderRadius:8,padding:'9px 14px',color:'#fff',
                fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit',flexShrink:0 }}>
              Send →
            </button>
          </>
        )}
      </div>

      {/* Bottom sheet — full request builder UI */}
      {open && (
        <div onClick={(e)=>e.target===e.currentTarget&&setOpen(false)}
          style={{ position:'fixed',inset:0,background:'rgba(15,23,42,0.5)',zIndex:70,
            backdropFilter:'blur(4px)',display:'flex',alignItems:'flex-end' }}>
          <div style={{ background:'#fff',width:'100%',maxHeight:'92vh',
            borderRadius:'16px 16px 0 0',display:'flex',flexDirection:'column',overflow:'hidden',
            animation:'slideUp 0.25s ease-out' }}>
            <div style={{ padding:'14px 20px',borderBottom:'1px solid #F1F5F9',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
              <div>
                <div style={{ fontSize:14,fontWeight:600,color:'#0F172A' }}>Your Request</div>
                <div style={{ fontSize:11,color:'#94A3B8',marginTop:2 }}>
                  Building your quote — {items.length} item{items.length===1?'':'s'}
                </div>
              </div>
              <button onClick={()=>setOpen(false)} aria-label="Close"
                style={{ background:'none',border:'none',cursor:'pointer',fontSize:22,color:'#94A3B8',padding:'0 4px',fontFamily:'inherit' }}>×</button>
            </div>
            <div style={{ display:'flex',flexDirection:'column',flex:1,minHeight:0 }}>
              <RequestBuilder
                items={items} logo={logo} notes={notes}
                onRemove={onRemove} onChangeQty={onChangeQty} onChangeColor={onChangeColor}
                onUploadLogo={onUploadLogo} onRemoveLogo={onRemoveLogo}
                onChangeNotes={onChangeNotes}
                onSend={()=>{setOpen(false);onSend();}}
                distName={distName}/>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Product modal ────────────────────────────────────────────────────────────
function ProductModal({ product: p, dist, isInRequest, onClose, onAdd }) {
  const [imgIdx, setImgIdx] = useState(0);
  const [imgError, setImgError] = useState(false);
  const gallery = useMemo(() => buildGallery(p), [p]);
  const description = useMemo(() => cleanDescription(p.description), [p.description]);
  const colors = p.colors || [];
  const currentImg = gallery[imgIdx];
  const showPricing = dist.show_pricing;

  const goPrev = (e) => { e.stopPropagation(); setImgError(false); setImgIdx(i => (i - 1 + gallery.length) % gallery.length); };
  const goNext = (e) => { e.stopPropagation(); setImgError(false); setImgIdx(i => (i + 1) % gallery.length); };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:100,
      display:'flex',alignItems:'flex-start',justifyContent:'center',padding:24,overflowY:'auto',backdropFilter:'blur(4px)' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:'#fff',borderRadius:16,width:'100%',maxWidth:680,overflow:'hidden',marginTop:24 }}>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 24px',borderBottom:'1px solid #E2E8F0' }}>
          <div style={{ fontSize:16,fontWeight:600,color:'#0F172A' }}>{p.name}</div>
          <button onClick={onClose} style={{ background:'none',border:'none',cursor:'pointer',fontSize:22,color:'#94A3B8',padding:'0 4px',fontFamily:'inherit' }}>×</button>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:24,padding:24 }}>
          <div>
            <div style={{ position:'relative',height:240,borderRadius:12,background:'#F8FAFC',
              display:'flex',alignItems:'center',justifyContent:'center',marginBottom:14,overflow:'hidden' }}>
              {currentImg && !imgError ? (
                <img src={currentImg.url} alt={p.name} onError={()=>setImgError(true)}
                  style={{ maxWidth:'100%',maxHeight:'100%',objectFit:'contain' }}/>
              ) : (
                <span style={{ fontSize:80 }}>{EMOJIS[p.category]||'📦'}</span>
              )}
              {gallery.length > 1 && (
                <>
                  <button onClick={goPrev} aria-label="Previous"
                    style={{ position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',
                      width:32,height:32,borderRadius:'50%',border:'1px solid #E2E8F0',
                      background:'rgba(255,255,255,0.9)',cursor:'pointer',fontSize:16,color:'#475569',
                      display:'flex',alignItems:'center',justifyContent:'center',padding:0,fontFamily:'inherit' }}>‹</button>
                  <button onClick={goNext} aria-label="Next"
                    style={{ position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',
                      width:32,height:32,borderRadius:'50%',border:'1px solid #E2E8F0',
                      background:'rgba(255,255,255,0.9)',cursor:'pointer',fontSize:16,color:'#475569',
                      display:'flex',alignItems:'center',justifyContent:'center',padding:0,fontFamily:'inherit' }}>›</button>
                  <div style={{ position:'absolute',bottom:8,left:0,right:0,display:'flex',justifyContent:'center',gap:5 }}>
                    {gallery.map((_,i)=>(
                      <div key={i} style={{ width:6,height:6,borderRadius:'50%',background:i===imgIdx?G:'#CBD5E1' }}/>
                    ))}
                  </div>
                  <div style={{ position:'absolute',top:8,right:8,background:'rgba(15,23,42,0.7)',color:'#fff',fontSize:10,padding:'2px 8px',borderRadius:10,fontWeight:500 }}>
                    {imgIdx+1} / {gallery.length}
                  </div>
                </>
              )}
            </div>
            {colors.length>0 && (
              <div>
                <div style={{ fontSize:11,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:8 }}>
                  Available in {colors.length} color{colors.length===1?'':'s'}
                </div>
                <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
                  {colors.map((c,i)=>(
                    <div key={i} title={c.name||c.n||''}
                      style={{ width:20,height:20,borderRadius:'50%',
                        background: hexBg(c.hex, '', '#ccc'),
                        border:'1px solid #E2E8F0' }}/>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div>
            <div style={{ fontSize:13,color:'#475569',lineHeight:1.6,marginBottom:14 }}>{description}</div>
            <div style={{ display:'flex',gap:8,flexWrap:'wrap',marginBottom:18 }}>
              {p.lead_time && <span style={{ background:`${G}18`,color:'#059669',fontSize:11,padding:'3px 9px',borderRadius:6 }}>⏱ {p.lead_time}</span>}
              {p.min_qty && <span style={{ background:'#F1F5F9',color:'#475569',fontSize:11,padding:'3px 9px',borderRadius:6 }}>Min order: {p.min_qty}</span>}
            </div>
            {showPricing && p.pricing?.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:11,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:8 }}>Pricing</div>
                <table style={{ width:'100%',borderCollapse:'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ fontSize:10,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',padding:'5px 8px',textAlign:'left',borderBottom:'1px solid #F1F5F9' }}>Quantity</th>
                      <th style={{ fontSize:10,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',padding:'5px 8px',textAlign:'right',borderBottom:'1px solid #F1F5F9' }}>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.pricing.map((t,i)=>(
                      <tr key={i}>
                        <td style={{ padding:'6px 8px',fontSize:12,borderBottom:'1px solid #F8FAFC' }}>{t.qty||t.q}+</td>
                        <td style={{ padding:'6px 8px',fontSize:12,fontWeight:600,color:G,textAlign:'right',borderBottom:'1px solid #F8FAFC' }}>
                          ${(t.list||t.l||0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ fontSize:11,color:'#94A3B8',marginTop:8,fontStyle:'italic' }}>
                  Final pricing depends on quantity, decoration, and shipping. Request a quote for an exact total.
                </div>
              </div>
            )}
            {p.decoration_methods?.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:11,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:6 }}>Decoration options</div>
                <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
                  {p.decoration_methods.map(d=>(
                    <span key={d} style={{ background:'#F1F5F9',borderRadius:6,padding:'3px 9px',fontSize:11,color:'#475569' }}>{d}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div style={{ padding:'14px 24px',borderTop:'1px solid #E2E8F0',display:'flex',gap:10,justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:8,padding:'9px 18px',fontSize:13,cursor:'pointer',fontFamily:'inherit',color:'#64748B' }}>Close</button>
          <button onClick={()=>{onAdd(p);onClose();}} disabled={isInRequest}
            style={{ background: isInRequest ? '#94A3B8' : G,
              border:'none',borderRadius:8,padding:'9px 20px',color:'#fff',fontSize:13,fontWeight:500,
              cursor: isInRequest ? 'default' : 'pointer',fontFamily:'inherit' }}>
            {isInRequest ? '✓ Added to Request' : '+ Add to Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Final contact form (after Send Request clicked) ─────────────────────────
function ContactForm({ slug, items, logo, notes, onClose, onSuccess }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!name.trim() || !email.trim()) { setError('Name and email are required'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(`/api/storefront/${slug}/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: name,
          client_email: email,
          client_phone: phone,
          client_company: company,
          client_logo: logo,
          event_date: eventDate || null,
          notes,
          items: items.map(i => ({ id: i.id, name: i.name, qty: i.qty || null, color: i.color || null, image: i.image || null })),
        }),
      });
      if (!r.ok) throw new Error('Server error');
      onSuccess();
    } catch (e) {
      setError('Could not send. Please try again or contact us directly.');
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:120,
      display:'flex',alignItems:'flex-start',justifyContent:'center',padding:24,overflowY:'auto',backdropFilter:'blur(4px)' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:'#fff',borderRadius:16,width:'100%',maxWidth:480,overflow:'hidden',marginTop:24 }}>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 24px',borderBottom:'1px solid #E2E8F0' }}>
          <div>
            <div style={{ fontSize:16,fontWeight:600,color:'#0F172A' }}>Almost there</div>
            <div style={{ fontSize:12,color:'#94A3B8',marginTop:2 }}>Where should we send the quote?</div>
          </div>
          <button onClick={onClose} style={{ background:'none',border:'none',cursor:'pointer',fontSize:22,color:'#94A3B8',padding:'0 4px',fontFamily:'inherit' }}>×</button>
        </div>
        <div style={{ padding:24 }}>
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12,fontWeight:500,color:'#374151',marginBottom:5,display:'block' }}>
              Your name <span style={{ color:'#EF4444' }}>*</span>
            </label>
            <input value={name} onChange={e=>setName(e.target.value)} placeholder="Jane Smith"
              style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'10px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',boxSizing:'border-box' }}/>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12,fontWeight:500,color:'#374151',marginBottom:5,display:'block' }}>
              Email <span style={{ color:'#EF4444' }}>*</span>
            </label>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="jane@company.com"
              style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'10px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',boxSizing:'border-box' }}/>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12,fontWeight:500,color:'#374151',marginBottom:5,display:'block' }}>Phone</label>
            <input type="tel" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="(555) 555-0100"
              style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'10px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',boxSizing:'border-box' }}/>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12,fontWeight:500,color:'#374151',marginBottom:5,display:'block' }}>Company</label>
            <input value={company} onChange={e=>setCompany(e.target.value)} placeholder="Acme Corp"
              style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'10px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',boxSizing:'border-box' }}/>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12,fontWeight:500,color:'#374151',marginBottom:5,display:'block' }}>Event date (optional)</label>
            <input type="date" value={eventDate} onChange={e=>setEventDate(e.target.value)}
              style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'10px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',boxSizing:'border-box' }}/>
          </div>
          {error && <div style={{ fontSize:12,color:'#EF4444',marginBottom:12 }}>{error}</div>}
        </div>
        <div style={{ padding:'14px 24px',borderTop:'1px solid #E2E8F0',display:'flex',gap:10,justifyContent:'flex-end' }}>
          <button onClick={onClose} disabled={submitting} style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:8,padding:'10px 18px',fontSize:13,cursor:'pointer',fontFamily:'inherit',color:'#64748B' }}>Back</button>
          <button onClick={submit} disabled={submitting}
            style={{ background:G,border:'none',borderRadius:8,padding:'10px 22px',color:'#fff',fontSize:13,fontWeight:600,cursor:submitting?'wait':'pointer',fontFamily:'inherit',opacity:submitting?0.7:1 }}>
            {submitting ? 'Sending...' : 'Send Request →'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SuccessModal({ onClose, distName }) {
  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:130,
      display:'flex',alignItems:'center',justifyContent:'center',padding:24,backdropFilter:'blur(4px)' }}>
      <div style={{ background:'#fff',borderRadius:16,padding:36,maxWidth:420,textAlign:'center' }}>
        <div style={{ fontSize:48,marginBottom:8 }}>✅</div>
        <div style={{ fontSize:18,fontWeight:600,color:'#0F172A',marginBottom:8 }}>Request sent</div>
        <div style={{ fontSize:13,color:'#64748B',lineHeight:1.6,marginBottom:20 }}>
          Your request was sent to {distName||'the team'}. They'll respond within 24 hours with detailed pricing.
        </div>
        <button onClick={onClose}
          style={{ background:G,border:'none',borderRadius:8,padding:'10px 28px',color:'#fff',fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
          Done
        </button>
      </div>
    </div>
  );
}

// ─── Product card (with + button + flying-image animation) ───────────────────
// ─── Pagination ───────────────────────────────────────────────────────────────
// Simple page selector — handles up to ~20 pages cleanly. For larger ranges
// we condense with ellipses (1 … 4 5 6 … 20). Server caps the catalog response
// at 50 products which is well within sensible bounds for promo distributors.
function Pagination({ page, totalPages, onChange, accent }) {
  // Build the visible page numbers list, with ellipses to keep things compact
  const pages = [];
  const add = (n) => { if (!pages.includes(n)) pages.push(n); };
  add(1);
  for (let i = page - 1; i <= page + 1; i++) {
    if (i > 1 && i < totalPages) add(i);
  }
  add(totalPages);
  // Now insert ellipses where there are gaps
  const withGaps = [];
  for (let i = 0; i < pages.length; i++) {
    withGaps.push(pages[i]);
    if (i < pages.length - 1 && pages[i+1] - pages[i] > 1) withGaps.push('…');
  }

  const btnBase = {
    minWidth: 36, height: 36, padding: '0 10px', borderRadius: 8,
    border: '1px solid #E2E8F0', background: '#fff', color: '#475569',
    fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
  };

  return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:6,marginTop:32,marginBottom:16,flexWrap:'wrap' }}>
      <button onClick={()=>onChange(page-1)} disabled={page===1}
        style={{ ...btnBase, opacity:page===1?0.4:1, cursor:page===1?'not-allowed':'pointer' }}>
        ← Prev
      </button>
      {withGaps.map((p, i) => p === '…'
        ? <span key={`g${i}`} style={{ padding:'0 6px',color:'#94A3B8',fontSize:13 }}>…</span>
        : <button key={p} onClick={()=>onChange(p)}
            style={{ ...btnBase,
              background: p===page ? accent : '#fff',
              color: p===page ? '#fff' : '#475569',
              borderColor: p===page ? accent : '#E2E8F0',
              fontWeight: p===page ? 700 : 500,
            }}>{p}</button>
      )}
      <button onClick={()=>onChange(page+1)} disabled={page===totalPages}
        style={{ ...btnBase, opacity:page===totalPages?0.4:1, cursor:page===totalPages?'not-allowed':'pointer' }}>
        Next →
      </button>
    </div>
  );
}

function PublicProductCard({ p, dist, isInRequest, onClick, onAdd, cardRef }) {
  const [imgError, setImgError] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const imgUrl = cardImageUrl(p);
  const showPricing = dist.show_pricing;
  const startingPrice = showPricing && p.pricing?.length > 0
    ? Math.min(...p.pricing.map(t => t.list || t.l || Infinity))
    : null;
  const accent = dist.brand_color || G;

  const handleAdd = (e) => {
    e.stopPropagation();
    if (isInRequest) return;
    onAdd(p, e.currentTarget.closest('[data-card]'));
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 600);
  };

  // Card border + shadow: layered state machine.
  //   - isInRequest = green ring (always wins)
  //   - hovered = accent border + subtle lift
  //   - default = hairline gray border
  // transition handles the gentle hover transition.
  const borderColor = isInRequest ? accent : (hovered ? accent : '#EEEDE9');
  const shadow = isInRequest ? `0 0 0 2px ${accent}22`
                : hovered     ? '0 4px 12px rgba(15,23,42,0.06)'
                              : 'none';
  const lift = hovered && !isInRequest ? 'translateY(-2px)' : 'translateY(0)';

  return (
    <div ref={cardRef} data-card onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ background:'#fff',borderRadius:12,
        border:`1px solid ${borderColor}`,
        boxShadow: shadow,
        transform: lift,
        overflow:'hidden',cursor:'pointer',
        transition:'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
        position:'relative' }}>
      <div style={{ height:180,background:'#F8FAFC',display:'flex',alignItems:'center',justifyContent:'center',fontSize:60,overflow:'hidden',position:'relative' }}>
        {imgUrl && !imgError ? (
          <img src={imgUrl} alt={p.name} onError={()=>setImgError(true)}
            style={{ maxWidth:'100%',maxHeight:'100%',objectFit:'contain' }}/>
        ) : (
          <span>{EMOJIS[p.category]||'📦'}</span>
        )}
        {/* Add button */}
        <button onClick={handleAdd} aria-label={isInRequest ? 'Already added' : 'Add to request'}
          style={{ position:'absolute',top:10,right:10,
            width:34,height:34,borderRadius:'50%',
            border:'none',
            background: justAdded || isInRequest ? accent : 'rgba(255,255,255,0.95)',
            color: justAdded || isInRequest ? '#fff' : '#0F172A',
            cursor: isInRequest ? 'default' : 'pointer',
            fontSize:18,fontWeight:600,
            display:'flex',alignItems:'center',justifyContent:'center',padding:0,fontFamily:'inherit',
            boxShadow:'0 2px 8px rgba(15,23,42,0.12)',
            transition:'all 0.2s' }}>
          {justAdded || isInRequest ? '✓' : '+'}
        </button>
      </div>
      <div style={{ padding:14 }}>
        <div style={{ fontSize:14,fontWeight:500,color:'#0F172A',marginBottom:6,lineHeight:1.3,minHeight:36 }}>{p.name}</div>
        {startingPrice != null && isFinite(startingPrice) && (
          <div style={{ fontSize:13,color:accent,fontWeight:600 }}>
            From ${startingPrice.toFixed(2)}
          </div>
        )}
        <div style={{ fontSize:11,color:'#94A3B8',marginTop:4 }}>
          {p.colors?.length > 0 && `${p.colors.length} colors · `}{p.category||'Product'}
        </div>
      </div>
    </div>
  );
}

// ─── Main public storefront ──────────────────────────────────────────────────
export default function PublicStorefront({ slug }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  // Browse-all toggle: hidden by default so the homepage stays focused on
  // featured products. The user clicks "Browse all products" to reveal the
  // full grid, then can paginate through it 50 per page.
  const [browseAll, setBrowseAll] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [showContact, setShowContact] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= DESKTOP_BREAKPOINT : true);

  // Request state — persisted to sessionStorage so refresh doesn't lose progress
  const [items, setItems] = useState([]);
  const [logo, setLogo] = useState(null);
  const [notes, setNotes] = useState('');

  // Load persisted state on mount
  useEffect(() => {
    try {
      const i = sessionStorage.getItem(STORAGE_KEY_ITEMS);
      const l = sessionStorage.getItem(STORAGE_KEY_LOGO);
      const n = sessionStorage.getItem(STORAGE_KEY_NOTES);
      if (i) setItems(JSON.parse(i));
      if (l) setLogo(l);
      if (n) setNotes(n);
    } catch {}
  }, []);

  // Persist on change
  useEffect(() => { try { sessionStorage.setItem(STORAGE_KEY_ITEMS, JSON.stringify(items)); } catch {} }, [items]);
  useEffect(() => { try { logo ? sessionStorage.setItem(STORAGE_KEY_LOGO, logo) : sessionStorage.removeItem(STORAGE_KEY_LOGO); } catch {} }, [logo]);
  useEffect(() => { try { sessionStorage.setItem(STORAGE_KEY_NOTES, notes); } catch {} }, [notes]);

  // Listen to viewport changes for desktop/mobile switch
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Fetch storefront data — re-runs whenever search OR page changes. Server
  // does the heavy lifting: filtering for search, slicing for pagination.
  // 300ms debounce while typing; AbortController prevents stale responses
  // from overwriting newer ones.
  const isFirstLoadRef = useRef(true);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    const q = search.trim();
    const delay = isFirstLoadRef.current ? 0 : 300;

    const timer = setTimeout(() => {
      if (!isFirstLoadRef.current) setSearching(true);
      // Build URL with page + limit params. Server returns the full response
      // shape regardless of mode (search/curated/catalog) so the client
      // doesn't have to special-case.
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      params.set('page', String(page));
      params.set('limit', String(PAGE_SIZE));
      const url = `/api/storefront/${slug}?${params.toString()}`;
      fetch(url, { signal: controller.signal })
        .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .then(d => {
          setData(d);
          if (isFirstLoadRef.current) {
            setLoading(false);
            isFirstLoadRef.current = false;
          }
          setSearching(false);
        })
        .catch(e => {
          if (e.name === 'AbortError') return; // newer query already firing
          if (isFirstLoadRef.current) {
            setError(e.message);
            setLoading(false);
            isFirstLoadRef.current = false;
          }
          setSearching(false);
        });
    }, delay);

    return () => { clearTimeout(timer); controller.abort(); };
  }, [slug, search, page]);

  // Reset to page 1 whenever the search term changes (otherwise users could
  // type a new query and stay on page 7 of the previous search's results).
  useEffect(() => { setPage(1); }, [search]);

  const itemIds = useMemo(() => new Set(items.map(i => i.id)), [items]);

  // Add product to request — runs the fly-in animation
  const flyTo = useCallback((sourceEl) => {
    if (!sourceEl) return;
    // Find target: rail (desktop) or bottom bar (mobile)
    const target = document.querySelector('[data-fly-target]');
    if (!target) return;
    const sRect = sourceEl.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();
    const ghost = document.createElement('div');
    ghost.style.cssText = `
      position:fixed;
      left:${sRect.left + sRect.width/2 - 24}px;
      top:${sRect.top + 24}px;
      width:48px;height:48px;border-radius:8px;
      background:${G};
      box-shadow:0 8px 24px rgba(16,185,129,0.4);
      z-index:200;
      display:flex;align-items:center;justify-content:center;
      color:#fff;font-size:22px;font-weight:600;
      transition:all 0.55s cubic-bezier(0.4, 0, 0.2, 1);
      pointer-events:none;
    `;
    ghost.textContent = '✓';
    document.body.appendChild(ghost);
    requestAnimationFrame(() => {
      ghost.style.left = `${tRect.left + tRect.width/2 - 12}px`;
      ghost.style.top = `${tRect.top + tRect.height/2 - 12}px`;
      ghost.style.width = '24px';
      ghost.style.height = '24px';
      ghost.style.opacity = '0';
      ghost.style.fontSize = '12px';
    });
    setTimeout(() => ghost.remove(), 600);
  }, []);

  const addItem = useCallback((p, sourceEl) => {
    setItems(prev => prev.find(i => i.id === p.id) ? prev : [...prev, {
      id: p.id,
      name: p.name,
      category: p.category,
      image: cardImageUrl(p),
      colors: p.colors || [],
      qty: null,
      color: null,
    }]);
    flyTo(sourceEl);
  }, [flyTo]);

  const removeItem = useCallback((id) => {
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const setItemQty = useCallback((id, qty) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, qty } : i));
  }, []);

  const setItemColor = useCallback((id, color) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, color } : i));
  }, []);

  const onSendRequest = useCallback(() => {
    if (items.length === 0) return;
    setShowContact(true);
  }, [items]);

  const onSuccess = () => {
    setShowContact(false);
    setShowSuccess(true);
    setItems([]);
    setLogo(null);
    setNotes('');
    try {
      sessionStorage.removeItem(STORAGE_KEY_ITEMS);
      sessionStorage.removeItem(STORAGE_KEY_LOGO);
      sessionStorage.removeItem(STORAGE_KEY_NOTES);
    } catch {}
  };

  // Products + pagination metadata come from the server. The client no
  // longer slices the array — it just renders what came back.
  const filtered = data?.products || [];
  const totalPages = data?.total_pages || 1;
  const totalProducts = data?.total_products || 0;
  const mode = data?.mode || 'catalog';

  if (loading) return <Loading />;
  if (error || !data) return <NotFound slug={slug} />;

  const dist = data.distributor;
  const featured = data.featured || [];
  // Per-distributor accent color, with safe fallback to platform default.
  // Validated server-side as a hex string, but double-check on the client too.
  const accent = (typeof dist.brand_color === 'string' && /^#[0-9A-Fa-f]{6}$/.test(dist.brand_color))
    ? dist.brand_color : G;
  // Logo source: prefer the base64-encoded logo_data (uploaded via Storefront
  // settings), fall back to the legacy logo_url field, then to an initial.
  // We detect whether logo_data is a full data URL (starts with "data:") or
  // raw base64. The settings page currently saves the full data URL, but older
  // rows or future changes might use either format — handle both.
  const logoSrc = dist.logo_data
    ? (typeof dist.logo_data === 'string' && dist.logo_data.startsWith('data:')
        ? dist.logo_data
        : `data:${dist.logo_mime || 'image/png'};base64,${dist.logo_data}`)
    : (dist.logo_url || null);
  const builderProps = {
    items, logo, notes,
    onRemove: removeItem,
    onChangeQty: setItemQty,
    onChangeColor: setItemColor,
    onUploadLogo: setLogo,
    onRemoveLogo: () => setLogo(null),
    onChangeNotes: setNotes,
    onSend: onSendRequest,
    distName: dist.company,
  };

  return (
    <div style={{ minHeight:'100vh',background:'#F8FAFC',fontFamily:"'DM Sans',system-ui,sans-serif",
      paddingBottom: !isDesktop ? (items.length > 0 ? 80 : 50) : 0 }}>

      {/* Inline keyframe for the bottom-sheet slide-up */}
      <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>

      {/* Sticky brand band: header (logo + contact) + hero (tagline + search)
          stay pinned to the top so the search bar is always reachable while
          users scroll the catalog underneath. */}
      <div style={{ position:'sticky',top:0,zIndex:50,background:'#fff',boxShadow:'0 1px 0 rgba(15,23,42,0.04)' }}>
        <header style={{ background:'#fff',borderBottom:'1px solid #EEEDE9' }}>
          <div style={{ maxWidth:1280,margin:'0 auto',padding:'16px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:16 }}>
            <div style={{ display:'flex',alignItems:'center',gap:12,minWidth:0 }}>
              {logoSrc ? (
                <img src={logoSrc} alt={dist.company}
                  style={{ width:36,height:36,borderRadius:6,objectFit:'contain',background:'#F8FAFC',flexShrink:0 }}/>
              ) : (
                <div style={{ width:36,height:36,borderRadius:6,background:accent,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:15,fontWeight:600,flexShrink:0 }}>
                  {(dist.company||'?')[0].toUpperCase()}
                </div>
              )}
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:15,fontWeight:600,color:'#0F172A',letterSpacing:'-0.2px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{dist.company}</div>
              </div>
            </div>
            {/* Header right side stays quiet — the hero handles the calls to
                action. A single "Browse catalog" anchor scrolls down to the
                product grid; we don't repeat phone/email here because they
                add clutter and the dedicated contact section (when present)
                is the right home for them. */}
            <a href="#catalog"
              onClick={e => { e.preventDefault(); document.getElementById('catalog')?.scrollIntoView({ behavior:'smooth', block:'start' }); }}
              style={{ fontSize:13,color:'#475569',textDecoration:'none',padding:'8px 0' }}>
              Browse catalog
            </a>
          </div>
        </header>

        {/* Hero — tinted band derived from the distributor's accent color so
            each storefront feels custom-branded without per-distributor code.
            8% opacity over white gives a faint wash that adapts to any accent
            (green → mint, magenta → blush, navy → soft sky). */}
        <section style={{ background:`${accent}14`,borderBottom:'1px solid #EEEDE9' }}>
          <div style={{ maxWidth:760,margin:'0 auto',padding:'56px 24px 52px',textAlign:'center' }}>
            {dist.hero_eyebrow && (
              <div style={{
                display:'inline-block',
                fontSize:11,fontWeight:600,color:accent,
                letterSpacing:'1.4px',textTransform:'uppercase',
                marginBottom:18,
              }}>
                {dist.hero_eyebrow}
              </div>
            )}
            <div style={{
              fontSize:36,fontWeight:700,color:'#0F172A',
              letterSpacing:'-1px',lineHeight:1.1,
              marginBottom:14,
            }}>
              {dist.hero_headline || dist.tagline || 'Branded merchandise that makes an impression.'}
              {dist.hero_headline_accent && (
                <> <span style={{ color:accent }}>{dist.hero_headline_accent}</span></>
              )}
            </div>
            <div style={{ fontSize:15,color:'#475569',maxWidth:480,margin:'0 auto 28px',lineHeight:1.5 }}>
              {dist.hero_subtext || 'Browse our catalog and request a quote — we\'ll respond within 24 hours.'}
            </div>

            {/* Search: pill input + inline accent button — single visual unit.
                Replaces the prior side-by-side input/button-light pattern with
                a cleaner combined component that reads as one action. */}
            <div style={{
              display:'flex',alignItems:'center',
              background:'#fff',
              border:'1px solid #E2E8F0',
              borderRadius:999,
              padding:'4px 4px 4px 18px',
              gap:8,maxWidth:440,margin:'0 auto',
              boxShadow:'0 1px 2px rgba(15,23,42,0.03)',
            }}>
              <input value={search} onChange={e=>setSearch(e.target.value)}
                placeholder="Search for products"
                style={{ flex:1,border:'none',outline:'none',background:'transparent',fontSize:14,color:'#0F172A',padding:'10px 0',fontFamily:'inherit' }}/>
              {searching ? (
                <div style={{ fontSize:11,color:'#94A3B8',padding:'0 14px' }}>Searching…</div>
              ) : (
                <button onClick={() => document.getElementById('catalog')?.scrollIntoView({ behavior:'smooth' })}
                  style={{ background:accent,color:'#fff',border:'none',borderRadius:999,padding:'9px 20px',fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
                  Search
                </button>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Main two-column layout (rail collapses on mobile) */}
      <div id="catalog" style={{ maxWidth:1280,margin:'0 auto',padding:'40px 24px',
        display:'flex',gap:24,alignItems:'flex-start' }}>

        {/* Product grid */}
        <main style={{ flex:1,minWidth:0 }}>
          {/* Featured products row — only on the default view (no active search) */}
          {!search.trim() && featured.length > 0 && (
            <section style={{ marginBottom:40 }}>
              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:11,color:'#94A3B8',textTransform:'uppercase',letterSpacing:'1.4px',fontWeight:600,marginBottom:4 }}>
                  Featured
                </div>
                <div style={{ fontSize:20,fontWeight:600,color:'#0F172A',letterSpacing:'-0.3px' }}>
                  Picked for the season
                </div>
              </div>
              <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:16 }}>
                {featured.map(p => (
                  <PublicProductCard key={`f-${p.id}`} p={p} dist={dist}
                    isInRequest={itemIds.has(p.id)}
                    onClick={()=>setSelected(p)}
                    onAdd={addItem}/>
                ))}
              </div>
            </section>
          )}

          {filtered.length === 0 ? (
            <div style={{ textAlign:'center',padding:'48px 24px',color:'#94A3B8' }}>
              <div style={{ fontSize:40,marginBottom:12 }}>🔍</div>
              <div style={{ fontSize:14 }}>
                {search.trim() ? `No products match "${search}"` : 'No products available yet — check back soon.'}
              </div>
            </div>
          ) : search.trim() ? (
            // ── SEARCH RESULTS — paginated server-side ─────────────────────
            <>
              <div style={{ fontSize:12,color:'#94A3B8',marginBottom:16 }}>
                {totalProducts.toLocaleString()} result{totalProducts===1?'':'s'} for "{search.trim()}"
                {totalPages > 1 && ` · page ${page} of ${totalPages}`}
              </div>
              <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:16 }}>
                {filtered.map(p => (
                  <PublicProductCard key={p.id} p={p} dist={dist}
                    isInRequest={itemIds.has(p.id)}
                    onClick={()=>setSelected(p)}
                    onAdd={addItem}/>
                ))}
              </div>
              {totalPages > 1 && (
                <Pagination page={page} totalPages={totalPages} accent={accent}
                  onChange={(p)=>{ setPage(p); window.scrollTo({ top:0, behavior:'smooth' }); }}/>
              )}
            </>
          ) : !browseAll ? (
            // ── DEFAULT VIEW: Browse-all CTA ───────────────────────────────
            // Featured row above already rendered. Now invite the user to
            // explore the full catalog if they're not searching.
            <div style={{ textAlign:'center',padding:'32px 24px',background:'#fff',
              border:'1px solid #E2E8F0',borderRadius:12 }}>
              <div style={{ fontSize:14,color:'#475569',marginBottom:14 }}>
                Looking for something specific? Try a search above, or browse the full catalog.
              </div>
              <button onClick={()=>{ setBrowseAll(true); setPage(1); }}
                style={{ background:accent,border:'none',borderRadius:10,padding:'11px 22px',
                  color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit' }}>
                Browse all products →
              </button>
            </div>
          ) : (
            // ── BROWSE-ALL: paginated full grid (server-driven) ────────────
            // Server returned `filtered` already sliced to this page, plus
            // total_products and total_pages metadata. We render directly.
            (() => {
              const start = (page - 1) * PAGE_SIZE;
              const end   = Math.min(start + filtered.length, totalProducts);
              return (
                <>
                  <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:14 }}>
                    <div style={{ width:4,height:18,background:'#CBD5E1',borderRadius:2 }}/>
                    <div style={{ fontSize:13,fontWeight:700,color:'#0F172A',textTransform:'uppercase',letterSpacing:'0.5px' }}>
                      {mode === 'curated' ? 'Catalog' : 'All products'}
                    </div>
                    <button onClick={()=>{ setBrowseAll(false); setPage(1); }}
                      style={{ marginLeft:'auto',background:'none',border:'none',color:accent,
                        fontSize:12,cursor:'pointer',fontFamily:'inherit',fontWeight:600 }}>
                      ← Back to featured
                    </button>
                  </div>
                  <div style={{ fontSize:12,color:'#94A3B8',marginBottom:16 }}>
                    Showing {(start + 1).toLocaleString()}–{end.toLocaleString()} of {totalProducts.toLocaleString()}
                  </div>
                  <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:16 }}>
                    {filtered.map(p => (
                      <PublicProductCard key={p.id} p={p} dist={dist}
                        isInRequest={itemIds.has(p.id)}
                        onClick={()=>setSelected(p)}
                        onAdd={addItem}/>
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <Pagination page={page} totalPages={totalPages} accent={accent}
                      onChange={(p)=>{ setPage(p); window.scrollTo({ top:0, behavior:'smooth' }); }}/>
                  )}
                </>
              );
            })()
          )}
        </main>

        {/* Right rail (desktop only) */}
        {isDesktop && (
          <div data-fly-target>
            <RequestRail {...builderProps} />
          </div>
        )}
      </div>

      {/* Footer */}
      <footer style={{ borderTop:'1px solid #E2E8F0',marginTop:24,padding:'24px',textAlign:'center',fontSize:11,color:'#94A3B8' }}>
        <div style={{ marginBottom:4 }}>
          Pricing varies based on decoration, quantity, and shipping. We'll confirm all charges before production.
        </div>
        <div>Powered by {BRAND.name}</div>
      </footer>

      {/* Mobile bar (always rendered when below breakpoint, drives the fly target via wrapper) */}
      {!isDesktop && (
        <div data-fly-target style={{ position:'fixed',bottom:0,left:0,right:0,zIndex:60,pointerEvents:'auto' }}>
          <MobileRequestBar {...builderProps}/>
        </div>
      )}

      {/* Modals */}
      {selected && (
        <ProductModal product={selected} dist={dist}
          isInRequest={itemIds.has(selected.id)}
          onClose={()=>setSelected(null)}
          onAdd={(p)=>addItem(p, document.querySelector(`[data-card]`))}/>
      )}
      {showContact && (
        <ContactForm slug={slug} items={items} logo={logo} notes={notes}
          onClose={()=>setShowContact(false)}
          onSuccess={onSuccess}/>
      )}
      {showSuccess && <SuccessModal onClose={()=>setShowSuccess(false)} distName={dist.company}/>}
    </div>
  );
}
